import { List, SingleColumnLayout, Spinner } from '@salecycle/ui-component-library';
import { post } from 'core/network/RestApi';
import { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { generatePath } from 'react-router';
import { Prompt, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useConsentClientName, useImportConsentContext } from '../context/ImportConsentContext';
import { FileManagementRoute } from '../routes';
import { Green } from '../types';
import { UploadError } from './UploadError';
import { UploadFile } from './UploadFile';
import { Fills, UploadStatusSvg } from './UploadStatusSvg';

const VerticalStack = styled.div`
  text-align: center;
`;

const Heading = styled.h1`
  font-size: 3.5rem;
  line-height: 4.2rem;
`;

const Subtext = styled.div`
  font-size: 1.4rem;
  margin: 4rem 0;
`;

const SpinnerContainer = styled.div`
  margin-bottom: 4rem;
  display: flex;
  justify-content: center;
`;

export interface IUploadFile {
  fileName: string;
  fileKey: string;
  signedUrl?: string;
  fileData: string;
}

interface IS3SignedUrlRequest {
  bucketName: string;
  keys: string[];
}

interface ISuccessResponseEntry {
  key: string;
  url: string;
}

interface ISuccessResponse {
  fileSignedUrls: ISuccessResponseEntry[];
}

const now = Date.now();
const bucketName = `salecycle-consent-history-import-${import.meta.env.VITE_ENV === 'production' ? 'prod' : 'poc'}`;

export function UploadFileList() {
  const { clientId, clientLevel, uploadFiles } = useImportConsentContext();
  const clientName = useConsentClientName();
  const { t } = useTranslation('fileManagement');
  const { replace } = useHistory();

  const [incomplete, incompleteDispatcher] = useReducer((state: number) => {
    return state - 1;
  }, uploadFiles.length);

  useEffect(() => {
    if (incomplete === 0) {
      replace(generatePath(FileManagementRoute.CLIENT_IMPORT_UPLOAD_SUCCESS, { clientLevel, clientId }), {
        successUploadCount: uploadFiles.length,
      });
    }
  }, [clientId, clientLevel, incomplete, replace, uploadFiles]);

  const uploadFileData = [];
  const uploadFileKeys = [];

  for (const cfd of uploadFiles) {
    const fileName = cfd.fileName.toLowerCase().replace(/\.(xlsx|csv)$/, `-$1-${now}.csv`);
    const fileKey = `${clientName}/${clientId}/${cfd.status.consentType}/${fileName}`;

    uploadFileKeys.push(fileKey);
    uploadFileData.push({
      fileName: cfd.fileName,
      fileKey,
      fileData: (cfd.status as Green).fileData,
    });
  }

  const { data: filesToUpload, isLoading, isError, error } = useQuery(
    uploadFileKeys,
    async () => {
      const result = await post<ISuccessResponse, IS3SignedUrlRequest>('/s3/presign/upload', {
        bucketName,
        keys: uploadFileKeys,
      });

      uploadFileData.forEach(datum => {
        datum.signedUrl = result?.data?.fileSignedUrls.find(resp => resp.key === datum.fileKey)?.url;
      });

      return uploadFileData;
    },
    { retry: false },
  );

  let fillType = Fills.DEFAULT;
  if (isError) {
    fillType = !(error as any).response ? Fills.ERROR : Fills.WARNING;
  }

  return (
    <SingleColumnLayout align="center">
      <VerticalStack>
        <Prompt when={!!incomplete} message={t('upload.warning.prompt')} />
        <UploadStatusSvg fill={fillType} />
        <Heading>
          {t('upload.title', { count: uploadFiles.length })}
          <br />
          {clientName}
        </Heading>
        <Subtext>{t('upload.warning.title')}</Subtext>
        {isLoading && (
          <>
            <SpinnerContainer role="figure" aria-label={t('upload.spinningLabel')}>
              <Spinner size="large" />
            </SpinnerContainer>
            {t('upload.preparing')}
          </>
        )}
        {isError && <UploadError error={error} />}
        {!isError && !isLoading && (
          <List>
            {filesToUpload.map((datum: IUploadFile) => (
              <UploadFile key={datum.fileKey} {...datum} onComplete={incompleteDispatcher} />
            ))}
          </List>
        )}
      </VerticalStack>
    </SingleColumnLayout>
  );
}
